/**
 * YÜZEN ASİSTAN — orb SIDEBAR'da yaşar; tık = SESLİ MOD (panel açılmaz, Geolop Asistanı gibi
 * konuşulur; ikinci tık kapatır). Yazılı panel sidebar'daki küçük "Sohbet" butonuyla
 * ya da /assistant sayfasıyla açılır. Sohbet çekirdeği panel KAPALIYKEN de takılı
 * kalır (display:none) — sesli döngü kesintisiz çalışır, asistan sayfa değişse de
 * kullanıcıyı takip eder.
 */
import { useLocation } from "wouter";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { AssistantChat } from "@/components/assistant-chat";
import { assistantStore, useAssistantStore } from "@/lib/assistant-store";
import { useShell } from "@/lib/ui-shell-store";

/**
 * PROAKTİF SABAH BRİFİNGİ — asistan İLK SÖZÜ SÖYLER: uygulama günün ilk açılışında
 * gece hareketlerini, açık önerileri ve günün planını balonla anlatır (kural tabanlı,
 * ücretsiz). "Asistanla konuş" sohbeti brifing istemiyle açar. Günde bir kez gösterilir.
 */
function BriefingCard() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const today = new Date().toLocaleDateString("sv-SE");
  const seenKey = `geolop:briefing:${activeDomainId}:${today}`;
  const [dismissed, setDismissed] = useState(false);
  const seen = dismissed || !activeDomainId || localStorage.getItem(seenKey) === "1";

  const { data } = useQuery({
    queryKey: ["assistant-briefing", activeDomainId, today],
    enabled: !seen,
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/assistant/briefing`);
      return r.ok ? (r.json() as Promise<{ hasContent: boolean; lines: string[] }>) : null;
    },
  });
  if (seen || !data?.hasContent) return null;
  const close = () => { localStorage.setItem(seenKey, "1"); setDismissed(true); };

  return (
    <div className="fixed bottom-6 right-6 z-[94] w-96 max-w-[calc(100vw-2rem)] rounded-2xl border bg-white p-4 shadow-xl dark:bg-gray-900" style={{ borderColor: "#f7b6b4" }}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#e83e3c" }}>🌅 Sabah Brifingi</span>
        <button onClick={close} className="text-[12px] text-muted-foreground hover:text-foreground">✕</button>
      </div>
      <ul className="space-y-1.5">
        {data.lines.slice(0, 4).map((l, i) => (
          <li key={i} className="flex gap-1.5 text-[12.5px] leading-snug">
            <span style={{ color: "#e83e3c" }}>•</span><span>{l}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button onClick={close} className="text-[12px] text-muted-foreground hover:text-foreground px-2 py-1">Tamam</button>
        <button onClick={() => { close(); assistantStore.ask("Sabah brifingine göre bugün öncelikli ne yapmalıyım? Kısaca yol göster."); }}
          className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-white" style={{ background: "#e83e3c" }}>
          Asistanla konuş
        </button>
      </div>
    </div>
  );
}

/**
 * BEYİN OLAY BALONU (v2-D) — biten ajan koşularının beyin yorumu: "Teknik denetim bitti,
 * sağlık 93, 26 hata — önce şununla başla." Dakikada bir yeni olay yoklanır; kullanıcı
 * "Tamam" deyince görüldü sayılır. Brifing kartıyla çakışmasın diye sıra brifingden sonra.
 */
function EventCard() {
  const [, setLocation] = useLocation();
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const queryKey = ["assistant-events", activeDomainId];
  const { data, refetch } = useQuery({
    queryKey,
    enabled: !!activeDomainId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/assistant/events`);
      return r.ok ? (r.json() as Promise<{ events: { id: number; kind: string; title: string; body: string; path: string | null }[] }>) : { events: [] };
    },
  });
  const ev = data?.events?.[0];
  // Aynı köşeyi brifing kartıyla paylaşır: brifing görülmeden olay balonu açılmaz (üst üste binmesin)
  const bugun = new Date().toLocaleDateString("sv-SE");
  const brifingGoruldu = !activeDomainId || localStorage.getItem(`geolop:briefing:${activeDomainId}:${bugun}`) === "1";
  if (!ev || !brifingGoruldu) return null;
  const markSeen = async () => { await fetch(`/api/assistant/events/${ev.id}/seen`, { method: "POST" }).catch(() => null); void refetch(); };

  return (
    <div className="fixed bottom-6 right-6 z-[93] w-96 max-w-[calc(100vw-2rem)] rounded-2xl border bg-white p-4 shadow-xl dark:bg-gray-900" style={{ borderColor: "rgba(232,62,60,0.35)" }}>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#e83e3c" }}>🧠 {ev.title}</span>
        <button onClick={() => void markSeen()} className="text-[12px] text-muted-foreground hover:text-foreground">✕</button>
      </div>
      <p className="text-[12.5px] leading-snug">{ev.body}</p>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button onClick={() => void markSeen()} className="text-[12px] text-muted-foreground hover:text-foreground px-2 py-1">Tamam</button>
        {ev.path && (
          <button onClick={() => { void markSeen(); setLocation(ev.path!); }}
            className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-white" style={{ background: "#e83e3c" }}>
            İncele
          </button>
        )}
      </div>
    </div>
  );
}

export function AssistantWidget() {
  const [location] = useLocation();
  const { open, tour } = useAssistantStore();
  const { expanded, hovered } = useShell(); // panel sidebar genişliğine göre kayar
  if (location === "/assistant") return null; // tam sayfa sohbet kendi çekirdeğini kullanır

  return (
    <>
    {/* SABAH BRİFİNGİ + BEYİN OLAYLARI — tur ya da panel açıkken araya girmez.
        Aynı köşeyi paylaşırlar: brifing görülmemişse önce o, sonra olay balonu. */}
    {!tour && !open && <BriefingCard />}
    {!tour && !open && <EventCard />}
    {/* PLATFORM TURU KARTI — global: hangi sayfada olursak olalım görünür */}
    {tour && (
      <div className="fixed bottom-6 right-6 z-[95] w-80 rounded-2xl border bg-white p-4 shadow-xl dark:bg-gray-900" style={{ borderColor: "#f7b6b4" }}>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#e83e3c" }}>
            Platform Turu · {tour.i + 1}/{tour.steps.length}
          </span>
          <button onClick={() => assistantStore.stopTour()} className="text-[11px] text-muted-foreground hover:text-foreground">Durdur ✕</button>
        </div>
        <div className="text-sm font-semibold">{tour.steps[tour.i].label}</div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">{tour.steps[tour.i].text}</div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
          <div className="h-full rounded-full transition-all" style={{ width: `${((tour.i + 1) / tour.steps.length) * 100}%`, background: "#e83e3c" }} />
        </div>
      </div>
    )}
    <div className="fixed z-50 rounded-2xl border shadow-theme-xl overflow-hidden flex-col bg-white dark:bg-gray-900 dark:border-gray-800 transition-all duration-300"
      style={{ left: expanded || hovered ? 302 : 102, bottom: 12, width: 400, height: "min(640px, calc(100vh - 24px))", borderColor: "rgba(232,62,60,0.25)", display: open ? "flex" : "none" }}>
      {/* Çarpı artık başlık satırında (onClose) — mutlak konumlu hali çöp ikonuna biniyordu */}
      <AssistantChat compact voiceControlled onClose={() => assistantStore.setOpen(false)} />
    </div>
    </>
  );
}
